import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadUser = useCallback(async () => {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        
        if (token && savedUser) {
            try {
                const response = await authAPI.me();
                setUser(response.data);
                localStorage.setItem('user', JSON.stringify(response.data));
            } catch (err) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setUser(null);
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const login = async (email, password) => {
        setError(null);
        try {
            const response = await authAPI.login({ email, password });
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('token', access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Error al iniciar sesión';
            setError(message);
            throw new Error(message);
        }
    };

    const register = async (userData) => {
        setError(null);
        try {
            const response = await authAPI.register(userData);
            const { access_token, user: newUser } = response.data;
            
            localStorage.setItem('token', access_token);
            localStorage.setItem('user', JSON.stringify(newUser));
            setUser(newUser);
            
            return newUser;
        } catch (err) {
            const message = err.response?.data?.detail || 'Error al registrarse';
            setError(message);
            throw new Error(message);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    const hasRole = (...roles) => {
        return user && roles.includes(user.rol);
    };

    const refreshUser = useCallback(async () => {
        try {
            const response = await authAPI.me();
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            return response.data;
        } catch { return null; }
    }, []);

    const value = {
        user,
        loading,
        error,
        login,
        register,
        logout,
        hasRole,
        refreshUser,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
